import React, { use, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { DateString } from '../../../../../common/src/date_string.ts';
import {
  BoundSongListEntry,
  CustomMusic,
  SongList,
  SongListEntry,
  SongListEntryType,
  SongListType,
  SongSlug,
  UnboundSongListEntry,
} from '../../../../../common/src/model.ts';
import { isBound } from '../../../../../common/src/model_util.ts';
import SongWarningIcon from '../../../assets/song-warning.svg?react';
import TextWarningIcon from '../../../assets/text-warning.svg?react';
import { HymnalWithHymns } from '../../../data/hymnals.ts';
import { getDisplayNameForSongListEntry } from '../../../util/path.ts';
import { Device, getDevice, getPlatform, Platform } from '../../../util/platform.ts';
import { SearchContext } from '../../../util/search_control.tsx';
import { CellPosition } from './planner.tsx';
import {
  DisplayUnauthorizedAlert,
  isAuthorizedToEditSongList,
  SongCellPadding,
  SongRowHeightDouble,
  SongRowHeightSingle,
  SongRowWidth,
} from './shared.tsx';
import { findSongListEntry, removeSongListEntry, replaceSongListEntry } from './song_list_rows.ts';
import { SongSelector, SongSelectorProps } from './song_selector.tsx';
import { TextCellEditor, TextCellEditorProps } from './text_cell_editor.tsx';
import { AllSongLists } from './use_all_song_lists.ts';

export const CellHoverOutlineWidth = '3px';

interface SongCellProps {
  allSongLists: AllSongLists;
  customMusic?: CustomMusic;
  date: DateString;
  hymnals?: HymnalWithHymns[];
  label?: string;
  songList: SongList;
  songListType: SongListType;
  songListEntryType: SongListEntryType;
  selectedCell?: CellPosition;
  setSelectedCell?: (cellPosition: CellPosition) => void;
  setIsDialogOpen?: (isDialogOpen: boolean) => void;
  moveDown?: () => void;
  isInHymnOfTheMonthPlanner?: boolean;
  isInternalUser: boolean;
  isOrgAdmin: boolean;
  isStaff?: boolean;
  setIsEditing?: (isEditing: boolean) => void;
  row: number;
  columnStart: number;
  columnCount: number;
  onSelectSong?: (songSlug?: SongSlug) => void;
}

export const SongCell = ({
  allSongLists,
  customMusic,
  date,
  hymnals,
  label,
  songList,
  songListType,
  songListEntryType,
  selectedCell,
  setIsEditing,
  setSelectedCell,
  setIsDialogOpen,
  moveDown,
  isInHymnOfTheMonthPlanner,
  isInternalUser,
  isOrgAdmin,
  isStaff,
  row,
  columnStart,
  columnCount,
  onSelectSong: onSelectSongParent,
}: SongCellProps) => {
  const [songSelectorProps, setSongSelectorProps] = useState<
    Pick<SongSelectorProps, 'selectSearchValue'> | undefined
  >();
  const [textCellEditorProps, setTextCellEditorProps] = useState<
    Pick<TextCellEditorProps, 'selectValue' | 'initialValue'> | undefined
  >();
  const { setSearchString } = use(SearchContext);
  const entry = findSongListEntry(songList, row);
  const editSongCellRef = useRef<HTMLDivElement>(null);

  const onCloseSongSelector = useCallback(() => {
    setSongSelectorProps(undefined);
  }, []);

  useEffect(() => {
    setIsEditing?.(Boolean(songSelectorProps || textCellEditorProps));
    // a cell that unmounts with its editor open (the grid is virtualized) must not leave the
    // planner believing an edit is in progress
    return () => setIsEditing?.(false);
  }, [setIsEditing, songSelectorProps, textCellEditorProps]);

  useEffect(() => {
    setIsDialogOpen?.(!!songSelectorProps);
  }, [setIsDialogOpen, songSelectorProps]);

  const updateSongListEntry = useCallback(
    (entry: SongListEntry) => {
      void allSongLists.update(replaceSongListEntry(songList, row, entry));
    },
    [allSongLists, row, songList],
  );

  const deleteSongListEntry = useCallback(() => {
    void allSongLists.update(removeSongListEntry(songList, row));
  }, [allSongLists, row, songList]);

  const onCloseTextCellEditor = useCallback(
    (text: string | undefined) => {
      setTextCellEditorProps(undefined);
      if (text === undefined) {
        return;
      }
      if (text === '') {
        deleteSongListEntry();
        return;
      }
      let newEntry: UnboundSongListEntry;
      if (entry) {
        const { slug: _, ...songNoSlug } = entry as BoundSongListEntry; // strip 'text' to replace with 'slug'
        newEntry = { ...songNoSlug, text };
      } else {
        newEntry = { row, text };
      }
      updateSongListEntry(newEntry);
    },
    [deleteSongListEntry, entry, row, updateSongListEntry],
  );

  const bindSongListEntry = useCallback(
    (slug: string) => {
      let newEntry: BoundSongListEntry;
      if (entry) {
        const { text: _, ...songNoText } = entry as UnboundSongListEntry; // strip 'text' to replace with 'slug'
        newEntry = { ...songNoText, slug };
      } else {
        newEntry = { row, slug };
      }
      updateSongListEntry(newEntry);
    },
    [entry, row, updateSongListEntry],
  );

  const onSelectSong = useCallback(
    (songSlug?: SongSlug) => {
      if (songSlug) {
        bindSongListEntry(songSlug);
      } else {
        deleteSongListEntry();
      }
      onCloseSongSelector();
      moveDown?.();
      onSelectSongParent?.();
    },
    [bindSongListEntry, deleteSongListEntry, moveDown, onCloseSongSelector, onSelectSongParent],
  );

  const onEditSongEntry = useCallback(() => {
    if (!isAuthorizedToEditSongList({ isOrgAdmin, isInternalUser, isStaff, songListType })) {
      void DisplayUnauthorizedAlert();
      return;
    }
    const initialValue = getDisplayNameForSongListEntry({ entry, hymnals, customMusic });
    if (songListEntryType !== SongListEntryType.Song) {
      setTextCellEditorProps({ initialValue, selectValue: true });
    } else {
      if (initialValue) {
        setSearchString(initialValue);
      }
      setSongSelectorProps({ selectSearchValue: true });
    }
  }, [
    customMusic,
    entry,
    hymnals,
    isInternalUser,
    isOrgAdmin,
    isStaff,
    setSearchString,
    songListEntryType,
    songListType,
  ]);

  // TODO(hewitt): isSelected is false because songList has start date 7/21 (Mon), but selected cell is 7/20 (Sun)
  //               selectedCell & selectedColumn should be based on the column index not a date
  const isSelected =
    selectedCell &&
    row === selectedCell.row &&
    selectedCell.column >= columnStart &&
    selectedCell.column < columnStart + columnCount;

  const handleCellMouseDown = useCallback(() => {
    // Only open the editor if the cell is both selected and active.
    // If the user clicks away from the liturgy planner and then clicks back into the selected cell, don't open.
    const isActive = editSongCellRef.current === document.activeElement;
    if (
      getPlatform() === Platform.Mobile ||
      (isSelected && isActive) ||
      (songListType === SongListType.HymnOfTheMonth && !isInHymnOfTheMonthPlanner)
    ) {
      setTimeout(onEditSongEntry); // allows song cell to get focus before the text editor attempts to take focus
      return;
    }

    if (setSelectedCell !== undefined && !isSelected) {
      setSelectedCell({ row, column: columnStart });
    }
  }, [columnStart, isInHymnOfTheMonthPlanner, isSelected, onEditSongEntry, row, setSelectedCell, songListType]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }

      if (!isAuthorizedToEditSongList({ isOrgAdmin, isInternalUser, isStaff, songListType })) {
        void DisplayUnauthorizedAlert();
        return;
      }

      const keyIsNumber = event.code.match(/^Digit[0-9]$/) || event.code.match(/^Numpad[0-9]$/);
      const keyIsLetter = event.code.match(/^Key[A-Z]$/);
      const isCopyPaste = (event.key === 'v' || event.key === 'c') && (event.metaKey || event.ctrlKey);

      if (isCopyPaste) {
        // avoid opening song selector or text editor on paste
        // either the text editor is already open and will accept the text, or we are doing a multi-cell paste
        return;
      }

      if (keyIsNumber || keyIsLetter) {
        if (songListEntryType === SongListEntryType.Song) {
          setSongSelectorProps({ selectSearchValue: false });
        } else {
          setTextCellEditorProps({ selectValue: false });
        }
        return;
      }

      switch (event.key) {
        case 'Enter':
          onEditSongEntry();
          break;
        case 'Backspace':
        case 'Delete':
          deleteSongListEntry();
          break;
      }
    },
    [isOrgAdmin, isInternalUser, songListType, isStaff, songListEntryType, onEditSongEntry, deleteSongListEntry],
  );

  useEffect(() => {
    if (isSelected && editSongCellRef.current) {
      editSongCellRef.current.focus();
    }
  }, [isSelected, songSelectorProps]);

  const isWideFormat = Boolean(columnCount && columnCount > 1);

  const isWarningVisible = Boolean(
    entry &&
    ((songListEntryType === SongListEntryType.Song && !isBound(entry)) ||
      (songListEntryType !== SongListEntryType.Song && isBound(entry))),
  );
  const warning = isWarningVisible && (isBound(entry) ? <TextWarning /> : <SongWarning />);

  const gridRow = row;
  const gridColumnStart = (columnStart ?? 0) + 1;
  const gridColumnEnd = gridColumnStart + (columnCount ?? 1);
  return (
    <>
      {songSelectorProps && (
        <SongSelector
          key="selector"
          songList={songList}
          onClose={onCloseSongSelector}
          onSongClick={onSelectSong}
          {...songSelectorProps}
        />
      )}
      <EditSongCell
        tabIndex={0}
        key={`${date}-${row}`}
        $isFixedWidth={!isWideFormat}
        $isDoubleHeight={!!label || getPlatform() === Platform.Mobile}
        onMouseDown={handleCellMouseDown}
        onKeyDown={onKeyDown}
        ref={editSongCellRef}
        style={{
          gridRow,
          gridColumnStart,
          gridColumnEnd,
          ...(columnCount && { width: `calc(${columnCount} * ${SongRowWidth})` }),
        }}
      >
        {textCellEditorProps ? (
          <TextCellEditor onClose={onCloseTextCellEditor} onEnter={moveDown} {...textCellEditorProps} />
        ) : (
          <SongCellContent $sticky={isWideFormat} $isInternalUser={isInternalUser}>
            {label && <CellLabel key="header">{label}</CellLabel>}
            <SongCellSongName $highlight={isBound(entry)}>
              {!hymnals?.length ? '' : getDisplayNameForSongListEntry({ entry: entry, hymnals, customMusic })}
            </SongCellSongName>
          </SongCellContent>
        )}
        {warning}
      </EditSongCell>
    </>
  );
};

const SongCellContent = styled.div<{ $sticky: boolean; $isInternalUser: boolean }>`
  color: var(--color-text);
  display: flex;
  flex-direction: column;
  ${(props) => (props.$sticky ? 'position: sticky;' : '')}
  ${(props) => (props.$sticky ? `left: ${getPlatform() === Platform.Mobile ? 0 : SongRowWidth};` : '')}
  padding: ${SongCellPadding};
  /* overcoming Apple's poor CSS support */
  ${getDevice() === Device.iOS ? 'padding-right: 0;' : ''};
  white-space: nowrap;
`;

const SongCellSongNameWidth = `${SongRowWidth} - (${SongCellPadding} * 2)`;

const SongCellSongName = styled.div<{ $highlight: boolean }>`
  max-width: calc(${SongCellSongNameWidth});
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(props) => (props.$highlight ? 'var(--color-date-highlight-text)' : 'var(--color-text)')};
`;

const EditSongCell = styled.div<{
  $isFixedWidth: boolean;
  $isDoubleHeight: boolean;
  $disabled?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  height: ${SongRowHeightSingle};
  ${(props) => props.$isFixedWidth && `width: ${SongRowWidth};`}
  /* outline instead of border takes up 0 space */
  outline: 1px solid var(--color-text-lighter);
  height: ${(props) => (props.$isDoubleHeight ? SongRowHeightDouble : SongRowHeightSingle)};
  ${(props) =>
    getPlatform() !== Platform.Mobile &&
    !props.$disabled &&
    `
    border: ${CellHoverOutlineWidth} solid transparent; // avoids text movement on selection
    &:hover {
      background: var(--color-background-selected-item);
      border: ${CellHoverOutlineWidth} solid var(--color-background-selected-item); // avoids white border on hover
      cursor: pointer;
    }
    &:focus-within {
      border: ${CellHoverOutlineWidth} solid var(--color-text);
      z-index: 2;
    }
  `}
`;

const CellLabel = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
`;

// TODO(hewitt): combine these into the same .svg file and set <title> content dynamically
//               problem with setting child textContent attribute is that it changes for all instances
//               problem with wrapping in an outer <svg><title>text</title><Warning/></svg> is the svg does not draw
const SongWarning = styled(SongWarningIcon)`
  position: absolute;
  width: 20px;
  height: 20px;
  right: 5px;
  stroke: var(--color-warning);
`;

const TextWarning = styled(TextWarningIcon)`
  position: absolute;
  width: 20px;
  height: 20px;
  right: 5px;
  stroke: var(--color-warning);
`;
